import { useState } from "react";
import axios from "axios";
import { Lock, User, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_radiance-intel/artifacts/21cw87nz_Rad%20Banner.jpg";
const LOGO_URL = "/logo.png";

export default function LoginPage({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await axios.post(`${API}/auth/login`, { username, password });
      onLogin(res.data.token, res.data.username);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-hero-wrapper" data-testid="login-page">
      {/* Full background image */}
      <div
        className="login-hero-bg"
        style={{ backgroundImage: `url(${HERO_IMAGE})` }}
      />
      {/* Gradient overlay */}
      <div className="login-hero-overlay" />

      {/* Content layer */}
      <div className="login-hero-content">
        {/* Left side - Branding */}
        <div className="login-hero-left">
          <div className="login-brand-content">
            <h1 className="login-brand-title" data-testid="login-brand-title">
              Nexus
            </h1>
            <p className="login-brand-subtitle">
              Intelligence That Drives Action
            </p>
            <p className="login-brand-description login-desktop-only">
              A unified AI-powered platform for real estate insights, performance tracking, and decision intelligence.
            </p>
            <div className="login-brand-divider login-desktop-only" />
            <div className="login-brand-stats login-desktop-only">
              <div className="login-brand-stat">
                <span className="login-brand-stat-value">6</span>
                <span className="login-brand-stat-label">Intelligence Modules</span>
              </div>
              <div className="login-brand-stat">
                <span className="login-brand-stat-value">AI</span>
                <span className="login-brand-stat-label">Powered Analytics</span>
              </div>
              <div className="login-brand-stat">
                <span className="login-brand-stat-value">24/7</span>
                <span className="login-brand-stat-label">Real-time Data</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Login card */}
        <div className="login-hero-right">
          <div className="login-glass-card" data-testid="login-glass-card">
            {/* Logo */}
            <div className="flex justify-center mb-6">
              <img
                src={LOGO_URL}
                alt="Nexus"
                className="object-contain"
                style={{ height: 'clamp(88px, 28vw, 120px)', width: 'clamp(88px, 28vw, 120px)' }}
                data-testid="login-logo"
              />
            </div>

            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Welcome Back
              </h2>
              <p className="text-sm text-slate-500 mt-1">Sign in to Nexus Platform</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Username</label>
                <div className={`login-input-wrapper ${focusedField === 'username' ? 'focused' : ''}`}>
                  <User className="login-input-icon" />
                  <input
                    type="text"
                    data-testid="login-username-input"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    onFocus={() => setFocusedField('username')}
                    onBlur={() => setFocusedField(null)}
                    className="login-input-field"
                    required
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wider">Password</label>
                <div className={`login-input-wrapper ${focusedField === 'password' ? 'focused' : ''}`}>
                  <Lock className="login-input-icon" />
                  <input
                    type="password"
                    data-testid="login-password-input"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    className="login-input-field"
                    required
                  />
                </div>
              </div>

              {error && (
                <div className="text-sm text-red-600 bg-red-50/80 rounded-lg p-3 border border-red-200/50 backdrop-blur-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                data-testid="login-submit-button"
                disabled={loading}
                className="login-submit-btn"
              >
                <span>{loading ? "Signing in..." : "Sign In"}</span>
                {!loading && <ArrowRight className="h-4 w-4 ml-2" />}
              </button>
            </form>

            <p className="text-[10px] text-slate-400 text-center mt-6 tracking-wide uppercase">
              Radiance Realty &middot; Nexus Platform v1.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
